/**
 * Shadow system for Bare Biologics
 * 
 * Production-ready elevation and shadow tokens
 */

// Base shadow values (following Material Design elevation principles)
export const shadows = [
  'none',
  // Elevation 1
  '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  // Elevation 2
  '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  // Elevation 3
  '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  // Elevation 4
  '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  // Elevation 5
  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  // Elevation 6
  '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  // Custom healthcare elevations
  '0 2px 8px rgba(0, 0, 0, 0.08)',
  '0 4px 12px rgba(0, 0, 0, 0.1)',
  '0 8px 20px rgba(0, 0, 0, 0.12)',
  '0 12px 40px rgba(0, 0, 0, 0.15)',
  '0 16px 60px rgba(0, 0, 0, 0.18)',
  '0 20px 80px rgba(0, 0, 0, 0.2)',
  '0 24px 100px rgba(0, 0, 0, 0.22)',
  '0 28px 120px rgba(0, 0, 0, 0.24)',
  '0 32px 140px rgba(0, 0, 0, 0.26)',
  '0 36px 160px rgba(0, 0, 0, 0.28)',
  '0 40px 180px rgba(0, 0, 0, 0.3)',
  '0 44px 200px rgba(0, 0, 0, 0.32)',
  '0 48px 220px rgba(0, 0, 0, 0.34)',
  '0 52px 240px rgba(0, 0, 0, 0.36)',
  '0 56px 260px rgba(0, 0, 0, 0.38)',
  '0 60px 280px rgba(0, 0, 0, 0.4)',
  '0 64px 300px rgba(0, 0, 0, 0.42)',
  '0 68px 320px rgba(0, 0, 0, 0.44)'
];

// Named shadow presets
export const shadowPresets = {
  // Card shadows
  card: {
    resting: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    hover: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    active: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },
  
  // Button shadows
  button: {
    resting: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
    hover: '0 4px 20px rgba(0, 0, 0, 0.15)',
    active: '0 2px 10px rgba(0, 0, 0, 0.12)',
    disabled: 'none',
  },
  
  // Input shadows
  input: {
    default: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
    focus: '0 0 0 3px rgba(13, 148, 136, 0.1)',
    error: '0 0 0 3px rgba(239, 68, 68, 0.1)',
    success: '0 0 0 3px rgba(16, 185, 129, 0.1)',
  },
  
  // Navigation shadows
  navigation: {
    appBar: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
    drawer: '4px 0 24px rgba(0, 0, 0, 0.12)',
    dropdown: '0 10px 20px rgba(0, 0, 0, 0.15), 0 3px 6px rgba(0, 0, 0, 0.10)',
  },
  
  // Modal/Dialog shadows
  modal: {
    backdrop: 'rgba(0, 0, 0, 0.5)',
    content: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  
  // Healthcare-specific shadows
  healthcare: {
    statCard: '0 4px 12px rgba(13, 148, 136, 0.15)',
    alertCard: '0 4px 12px rgba(239, 68, 68, 0.15)',
    successCard: '0 4px 12px rgba(16, 185, 129, 0.15)',
    vialCard: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
};

// Colored shadows for brand elements
export const coloredShadows = {
  primary: {
    light: '0 4px 14px rgba(13, 148, 136, 0.25)',
    medium: '0 4px 14px rgba(13, 148, 136, 0.4)',
    strong: '0 6px 20px rgba(13, 148, 136, 0.5)',
  },
  
  secondary: {
    light: '0 4px 14px rgba(92, 114, 92, 0.25)',
    medium: '0 4px 14px rgba(92, 114, 92, 0.4)',
    strong: '0 6px 20px rgba(92, 114, 92, 0.5)',
  },
  
  success: {
    light: '0 2px 8px rgba(16, 185, 129, 0.25)',
    medium: '0 2px 8px rgba(16, 185, 129, 0.4)',
    strong: '0 4px 12px rgba(16, 185, 129, 0.5)',
  },
  
  warning: {
    light: '0 2px 8px rgba(245, 158, 11, 0.25)',
    medium: '0 2px 8px rgba(245, 158, 11, 0.4)',
    strong: '0 4px 12px rgba(245, 158, 11, 0.5)',
  },
  
  error: {
    light: '0 2px 8px rgba(239, 68, 68, 0.25)',
    medium: '0 2px 8px rgba(239, 68, 68, 0.4)',
    strong: '0 4px 12px rgba(239, 68, 68, 0.5)',
  },
};

// Inset shadows for depth
export const insetShadows = {
  light: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
  medium: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
  strong: 'inset 0 4px 8px rgba(0, 0, 0, 0.15)',
  
  // For input fields
  inputDefault: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
  inputFocus: 'inset 0 1px 2px rgba(0, 0, 0, 0.075)',
};

// Text shadows for better readability
export const textShadows = {
  subtle: '0 1px 2px rgba(0, 0, 0, 0.1)',
  medium: '0 2px 4px rgba(0, 0, 0, 0.2)',
  strong: '0 4px 8px rgba(0, 0, 0, 0.3)',
  
  // For light text on dark backgrounds
  lightOnDark: '0 1px 3px rgba(0, 0, 0, 0.4)',
  
  // For healthcare status indicators
  glow: {
    success: '0 0 8px rgba(16, 185, 129, 0.6)',
    warning: '0 0 8px rgba(245, 158, 11, 0.6)',
    error: '0 0 8px rgba(239, 68, 68, 0.6)',
  },
};

// Shadow utilities
export const shadowUtils = {
  // Elevation helper
  elevation: (level) => shadows[level] || shadows[0],
  
  // Hover effect
  hoverShadow: (baseShadow = shadowPresets.card.resting) => ({
    boxShadow: baseShadow,
    transition: 'box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: shadowPresets.card.hover,
    },
  }),
  
  // Focus ring
  focusRing: (color = '#0d9488') => ({
    '&:focus': {
      outline: 'none',
      boxShadow: `0 0 0 3px ${color}20`,
    },
  }),
  
  // Layered shadow (for complex depth)
  layeredShadow: (...shadows) => shadows.join(', '),
};
